import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './ManagementReview.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const AGENDA_ITEMS = [
  { key: 'audit_results',       label: 'Results of Audits' },
  { key: 'previous_actions',    label: 'Status of Actions from Previous Review' },
  { key: 'objectives_review',   label: 'Extent to Which Objectives Have Been Met' },
  { key: 'customer_feedback',   label: 'Customer Satisfaction & Feedback' },
  { key: 'process_performance', label: 'Process Performance & Product Conformity' },
  { key: 'nonconformities',     label: 'Nonconformities & Corrective Actions' },
  { key: 'external_providers',  label: 'Performance of External Providers' },
  { key: 'resource_adequacy',   label: 'Adequacy of Resources' },
  { key: 'risks_opportunities', label: 'Effectiveness of Risk & Opportunity Actions' },
];

const OUTPUT_ITEMS = [
  { key: 'improvement_opportunities', label: 'Opportunities for Improvement' },
  { key: 'qms_changes',               label: 'Any Need for Changes to the QMS' },
  { key: 'resource_needs',            label: 'Resource Needs' },
];

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ManagementReview({ user }) {
  const [reviews, setReviews] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [audits, setAudits] = useState([]);
  const [ncrs, setNcrs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('details');
  const [wizardLoading, setWizardLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const nextYear = new Date();
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  const nextReviewDefault = nextYear.toISOString().slice(0, 10);

  const [form, setForm] = useState({
    title: `Management Review ${new Date().getFullYear()}`,
    date: new Date().toISOString().slice(0, 10),
    attendees: [],
    chaired_by: '',
    next_review_date: nextReviewDefault,
    status: 'draft',
    audit_results: '',
    previous_actions: '',
    customer_feedback: '',
    process_performance: '',
    nonconformities: '',
    external_providers: '',
    resource_adequacy: '',
    risks_opportunities: '',
    improvement_opportunities: '',
    qms_changes: 'None identified.',
    resource_needs: '',
    notes: '',
  });

  const [actions, setActions] = useState([]);
  const [objReview, setObjReview] = useState({});

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [revRes, objRes, userRes, audRes, ncrRes] = await Promise.all([
        axios.get(`${API}/management-reviews`),
        axios.get(`${API}/objectives`),
        axios.get(`${API}/users/all`),
        axios.get(`${API}/audits`),
        axios.get(`${API}/non-conformances`),
      ]);
      setReviews(revRes.data);
      setObjectives(objRes.data);
      setUsers(userRes.data);
      setAudits(audRes.data);
      setNcrs(ncrRes.data);

      // Load supplementary data — fail gracefully if any endpoint errors
      const [supRes, eqRes, fbRes, trainingRes] = await Promise.allSettled([
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/equipment`),
        axios.get(`${API}/customer-feedback`),
        axios.get(`${API}/training`),
      ]);

      const admins = userRes.data.filter(u => u.role === 'admin');
      setForm(prev => ({
        ...prev,
        attendees: admins.map(u => u.id),
        _suppliers: supRes.status === 'fulfilled' ? supRes.value.data : [],
        _equipment: eqRes.status === 'fulfilled' ? eqRes.value.data : [],
        _feedback:  fbRes.status === 'fulfilled' ? fbRes.value.data : [],
        _training:  trainingRes.status === 'fulfilled' ? trainingRes.value.data : [],
      }));
    } catch (err) { console.error('MR fetchAll error:', err); }
    finally { setLoading(false); }
  };

  const autoPopulateAgenda = async () => {
    setWizardLoading(true);
    try {
      // Get previous review actions if any
      const prevReview = reviews.find(r => r.status === 'completed');
      const prevActions = prevReview?.new_actions || [];

      const res = await axios.post(`${API}/analyse/management-review-agenda`, {
        audits: audits.map(a => ({
          reference_number: a.reference_number, title: a.title,
          type: a.type, completed_date: a.completed_date,
          result: a.result, auditing_body: a.auditing_body,
        })),
        ncrs: ncrs.map(n => ({
          reference_number: n.reference_number, title: n.title,
          severity: n.severity, status: n.status, source: n.source,
          corrective_action_due: n.corrective_action_due,
        })),
        objectives: objectives.map(o => ({
          reference_number: o.reference_number, title: o.title,
          status: o.status, target_date: o.target_date,
          category: o.category, current_value: o.current_value,
          target_value: o.target_value, unit: o.unit,
        })),
        suppliers: (form._suppliers || []).map(s => ({
          name: s.name, status: s.status, type: s.type,
          public_liability_expiry: s.public_liability_expiry,
          employers_liability_expiry: s.employers_liability_expiry,
        })),
        equipment: (form._equipment || []).map(e => ({
          name: e.name, type: e.type, status: e.status,
          requires_loler: e.requires_loler,
        })),
        customer_feedback: (form._feedback || []).map(f => ({
          type: f.type, status: f.status, received_date: f.received_date,
          description: f.description.substring(0, 100),
        })),
        training: (form._training || []).map(t => ({
          training_type: t.training_type, status: t.status,
          expiry_date: t.expiry_date,
        })),
        staff: users.map(u => ({
          name: `${u.first_name} ${u.last_name}`, role: u.role,
          job_title: u.job_title,
        })),
        previous_actions: prevActions,
      });
      setForm(prev => ({ ...prev, ...res.data }));
      setActiveSection('agenda');
    } catch (err) {
      alert('Could not auto-populate — please fill in manually.');
    } finally {
      setWizardLoading(false);
    }
  };

  const autoPopulateActions = async () => {
    setWizardLoading(true);
    try {
      const res = await axios.post(`${API}/analyse/management-review-actions`, {
        agenda: {
          audit_results: form.audit_results,
          process_performance: form.process_performance,
          nonconformities: form.nonconformities,
          external_providers: form.external_providers,
          resource_adequacy: form.resource_adequacy,
          risks_opportunities: form.risks_opportunities,
        },
        objectives: objectives.map(o => ({
          reference_number: o.reference_number, title: o.title,
          status: o.status, target_date: o.target_date,
        })),
        staff: users.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}`, role: u.role })),
      });
      const suggested = res.data.actions || [];
      setActions(suggested.map(a => ({
        description: a.description,
        owner_id: users.find(u => `${u.first_name} ${u.last_name}` === a.owner)?.id || '',
        due_date: a.due_date || '',
        status: 'open',
      })));
    } catch (err) {
      alert('Could not suggest actions — please add manually.');
    } finally {
      setWizardLoading(false);
    }
  };

  const generatePdf = async (reviewId) => {
    setGeneratingPdf(true);
    try {
      const res = await axios.post(`${API}/management-reviews/${reviewId}/generate-pdf`, {});
      window.open(res.data.url, '_blank');
    } catch (err) {
      alert('Error generating PDF: ' + err.message);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const userName = (id) => {
    const u = users.find(u => u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : '—';
  };

  const toggleAttendee = (id) => {
    setForm(prev => ({
      ...prev,
      attendees: prev.attendees.includes(id)
        ? prev.attendees.filter(a => a !== id)
        : [...prev.attendees, id]
    }));
  };

  const handleSave = async (status = 'draft') => {
    if (!form.date) { alert('Date is required.'); return; }
    setSaving(true);
    try {
      // Save objectives review as JSONB
      const objectivesReviewData = objectives.map(obj => ({
        id: obj.id,
        reference_number: obj.reference_number,
        title: obj.title,
        status_before: obj.status,
        status_after: objReview[obj.id]?.status || obj.status,
        comment: objReview[obj.id]?.comment || '',
      }));

      const payload = {
        ...form,
        status,
        objectives_review: objectivesReviewData,
        new_actions: actions.filter(a => a.description.trim()),
        chaired_by: form.chaired_by || null,
        next_review_date: form.next_review_date || null,
        created_by: user.id,
      };
      // Strip internal state fields
      delete payload._suppliers;
      delete payload._equipment;
      delete payload._feedback;
      delete payload._training;

      const res = await axios.post(`${API}/management-reviews`, payload);

      // Update objective statuses if changed
      for (const obj of objectives) {
        const review = objReview[obj.id];
        if (review?.status && review.status !== obj.status) {
          await axios.patch(`${API}/objectives/${obj.id}`, {
            status: review.status,
            updated_by: user.id,
          });
        }
      }

      setView('list');
      fetchAll();
    } catch (err) { alert('Error saving: ' + err.message); }
    finally { setSaving(false); }
  };

  const managementReviewObjectives = useMemo(() =>
    objectives.filter(o => o.management_review), [objectives]);

  const allObjectives = objectives;

  if (loading) return <div className="mr-loading">Loading management reviews...</div>;

  // ── NEW REVIEW FORM ──────────────────────────────────────────
  if (view === 'new') return (
    <div className="mr-form-page">
      <button className="mr-back" onClick={() => setView('list')}>← MANAGEMENT REVIEWS</button>

      <div className="mr-form-header">
        <h2>NEW MANAGEMENT REVIEW</h2>
        <p>Complete each section of the agenda and record decisions and actions</p>
      </div>

      {/* Section tabs */}
      <div className="mr-tabs">
        {['details', 'agenda', 'objectives', 'actions', 'outputs'].map(tab => (
          <button key={tab} className={`mr-tab ${activeSection === tab ? 'active' : ''}`} onClick={() => setActiveSection(tab)}>
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {/* DETAILS */}
      {activeSection === 'details' && (
        <div className="mr-section">
          <div className="mr-form-grid">
            <div className="mr-field full">
              <label>TITLE</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="mr-field">
              <label>DATE *</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="mr-field">
              <label>NEXT REVIEW DATE</label>
              <input type="date" value={form.next_review_date} onChange={e => setForm({ ...form, next_review_date: e.target.value })} />
            </div>
            <div className="mr-field">
              <label>CHAIRED BY</label>
              <select value={form.chaired_by} onChange={e => setForm({ ...form, chaired_by: e.target.value })}>
                <option value="">— Select —</option>
                {users.filter(u => !u.is_archived).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
              </select>
            </div>
            <div className="mr-field full">
              <label>ATTENDEES</label>
              <div className="mr-attendees">
                {users.filter(u => !u.is_archived).map(u => (
                  <label key={u.id} className={`mr-attendee-chip ${form.attendees.includes(u.id) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={form.attendees.includes(u.id)} onChange={() => toggleAttendee(u.id)} style={{ display: 'none' }} />
                    {u.first_name} {u.last_name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mr-nav-actions">
            <button className="mr-btn primary" onClick={() => setActiveSection('agenda')}>NEXT: AGENDA →</button>
          </div>
        </div>
      )}

      {/* AGENDA */}
      {activeSection === 'agenda' && (
        <div className="mr-section">
          <div className="mr-wizard-bar">
            <span className="mr-wizard-hint">Fill in each section manually, or let Claude draft them from your Vera data.</span>
            <button className={`mr-wizard-btn ${wizardLoading ? 'loading' : ''}`} onClick={autoPopulateAgenda} disabled={wizardLoading}>
              {wizardLoading ? <><span className="mr-wizard-spinner" /> Generating...</> : '✦ Auto-populate from Vera'}
            </button>
          </div>
          {AGENDA_ITEMS.filter(a => a.key !== 'previous_actions' && a.key !== 'objectives_review').map(item => (
            <div key={item.key} className="mr-agenda-item">
              <div className="mr-agenda-label">{item.label}</div>
              <textarea
                className="mr-agenda-textarea"
                rows={4}
                value={form[item.key] || ''}
                onChange={e => setForm({ ...form, [item.key]: e.target.value })}
                placeholder={`Record discussion and findings for: ${item.label}`}
              />
            </div>
          ))}
          <div className="mr-nav-actions">
            <button className="mr-btn secondary" onClick={() => setActiveSection('details')}>← BACK</button>
            <button className="mr-btn primary" onClick={() => setActiveSection('objectives')}>NEXT: OBJECTIVES →</button>
          </div>
        </div>
      )}

      {/* OBJECTIVES REVIEW */}
      {activeSection === 'objectives' && (
        <div className="mr-section">
          <div className="mr-objectives-intro">
            Review the status of each objective and record comments. Changes will be saved to the live objectives tracker.
          </div>
          {allObjectives.length === 0 ? (
            <div className="mr-empty-obj">No objectives set yet — add them in the Objectives section first.</div>
          ) : (
            allObjectives.map(obj => (
              <div key={obj.id} className={`mr-obj-row ${obj.management_review ? 'is-mr' : ''}`}>
                <div className="mr-obj-info">
                  <div className="mr-obj-ref">{obj.reference_number} {obj.management_review && <span className="mr-tag">MR</span>}</div>
                  <div className="mr-obj-title">{obj.title}</div>
                  {obj.target_value && (
                    <div className="mr-obj-progress">
                      Progress: {obj.current_value || '0'} / {obj.target_value} {obj.unit || ''}
                    </div>
                  )}
                </div>
                <div className="mr-obj-controls">
                  <select
                    className="mr-obj-status"
                    value={objReview[obj.id]?.status || obj.status}
                    onChange={e => setObjReview(prev => ({ ...prev, [obj.id]: { ...prev[obj.id], status: e.target.value } }))}
                  >
                    <option value="not started">Not Started</option>
                    <option value="on track">On Track</option>
                    <option value="at risk">At Risk</option>
                    <option value="achieved">Achieved</option>
                    <option value="missed">Missed</option>
                  </select>
                  <input
                    className="mr-obj-comment"
                    placeholder="Comment on progress..."
                    value={objReview[obj.id]?.comment || ''}
                    onChange={e => setObjReview(prev => ({ ...prev, [obj.id]: { ...prev[obj.id], comment: e.target.value } }))}
                  />
                </div>
              </div>
            ))
          )}
          <div className="mr-nav-actions">
            <button className="mr-btn secondary" onClick={() => setActiveSection('agenda')}>← BACK</button>
            <button className="mr-btn primary" onClick={() => setActiveSection('actions')}>NEXT: ACTIONS →</button>
          </div>
        </div>
      )}

      {/* ACTIONS */}
      {activeSection === 'actions' && (
        <div className="mr-section">
          <div className="mr-wizard-bar">
            <span className="mr-wizard-hint">Add actions manually, or let Claude suggest them based on the agenda findings.</span>
            <button className={`mr-wizard-btn ${wizardLoading ? 'loading' : ''}`} onClick={autoPopulateActions} disabled={wizardLoading}>
              {wizardLoading ? <><span className="mr-wizard-spinner" />Generating...</> : '✦ Suggest Actions'}
            </button>
          </div>
          <div className="mr-actions-list">
            {actions.map((action, i) => (
              <div key={i} className="mr-action-row">
                <input
                  className="mr-action-input"
                  placeholder="Action description"
                  value={action.description}
                  onChange={e => {
                    const updated = [...actions];
                    updated[i].description = e.target.value;
                    setActions(updated);
                  }}
                />
                <select
                  className="mr-action-select"
                  value={action.owner_id}
                  onChange={e => {
                    const updated = [...actions];
                    updated[i].owner_id = e.target.value;
                    setActions(updated);
                  }}
                >
                  <option value="">— Owner —</option>
                  {users.filter(u => !u.is_archived).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
                <input
                  type="date"
                  className="mr-action-date"
                  value={action.due_date}
                  onChange={e => {
                    const updated = [...actions];
                    updated[i].due_date = e.target.value;
                    setActions(updated);
                  }}
                />
                <button className="mr-action-remove" onClick={() => setActions(actions.filter((_, j) => j !== i))}>✕</button>
              </div>
            ))}
            <button className="mr-btn secondary" onClick={() => setActions([...actions, { description: '', owner_id: '', due_date: '', status: 'open' }])}>
              + ADD ACTION
            </button>
          </div>
          <div className="mr-nav-actions">
            <button className="mr-btn secondary" onClick={() => setActiveSection('objectives')}>← BACK</button>
            <button className="mr-btn primary" onClick={() => setActiveSection('outputs')}>NEXT: OUTPUTS →</button>
          </div>
        </div>
      )}

      {/* OUTPUTS */}
      {activeSection === 'outputs' && (
        <div className="mr-section">
          <div className="mr-outputs-intro">Record the decisions and actions arising from this management review.</div>
          {OUTPUT_ITEMS.map(item => (
            <div key={item.key} className="mr-agenda-item">
              <div className="mr-agenda-label">{item.label}</div>
              <textarea
                className="mr-agenda-textarea"
                rows={4}
                value={form[item.key] || ''}
                onChange={e => setForm({ ...form, [item.key]: e.target.value })}
                placeholder={`Record ${item.label.toLowerCase()}...`}
              />
            </div>
          ))}
          <div className="mr-field" style={{ marginTop: 16 }}>
            <label className="mr-field-label">ADDITIONAL NOTES</label>
            <textarea className="mr-agenda-textarea" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
          </div>
          <div className="mr-nav-actions">
            <button className="mr-btn secondary" onClick={() => setActiveSection('actions')}>← BACK</button>
            <button className="mr-btn secondary" onClick={() => handleSave('draft')} disabled={saving}>SAVE AS DRAFT</button>
            <button className="mr-btn primary" onClick={() => handleSave('completed')} disabled={saving}>
              {saving ? 'SAVING...' : '✓ COMPLETE REVIEW'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── DETAIL VIEW ──────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const objReviewData = selected.objectives_review || [];
    return (
      <div className="mr-detail-page">
        <button className="mr-back" onClick={() => { setView('list'); setSelected(null); }}>← MANAGEMENT REVIEWS</button>
        <div className="mr-detail-header">
          <div className="mr-detail-ref">{selected.reference_number}</div>
          <h2 className="mr-detail-title">{selected.title}</h2>
          <div className="mr-detail-meta">
            <span className={`mr-status-badge ${selected.status === 'completed' ? 'completed' : 'draft'}`}>
              {selected.status === 'completed' ? 'Completed' : 'Draft'}
            </span>
            <span>{formatDate(selected.date)}</span>
            {selected.chaired_by && <span>Chaired by: {selected.chaired_by.first_name} {selected.chaired_by.last_name}</span>}
          </div>
          {selected.attendees?.length > 0 && (
            <div className="mr-detail-attendees">
              Present: {selected.attendees.map(id => userName(id)).join(', ')}
            </div>
          )}
        </div>

        {AGENDA_ITEMS.filter(a => a.key !== 'previous_actions' && a.key !== 'objectives_review').map(item => (
          selected[item.key] ? (
            <div key={item.key} className="mr-detail-block">
              <div className="mr-detail-block-title">{item.label}</div>
              <p className="mr-detail-block-text">{selected[item.key]}</p>
            </div>
          ) : null
        ))}

        {objReviewData.length > 0 && (
          <div className="mr-detail-block">
            <div className="mr-detail-block-title">Objectives Review</div>
            {objReviewData.map((obj, i) => (
              <div key={i} className="mr-obj-summary-row">
                <span className="mr-obj-summary-ref">{obj.reference_number}</span>
                <span className="mr-obj-summary-title">{obj.title}</span>
                <span className={`mr-obj-summary-status ${obj.status_after}`}>{obj.status_after}</span>
                {obj.comment && <span className="mr-obj-summary-comment">{obj.comment}</span>}
              </div>
            ))}
          </div>
        )}

        {OUTPUT_ITEMS.map(item => (
          selected[item.key] ? (
            <div key={item.key} className="mr-detail-block output">
              <div className="mr-detail-block-title">{item.label}</div>
              <p className="mr-detail-block-text">{selected[item.key]}</p>
            </div>
          ) : null
        ))}

        <div className="mr-detail-actions">
          {selected.minutes_url ? (
            <a href={selected.minutes_url} target="_blank" rel="noreferrer" className="mr-btn primary">VIEW PDF</a>
          ) : (
            <button className={`mr-btn primary ${generatingPdf ? 'disabled' : ''}`} onClick={() => generatePdf(selected.id)} disabled={generatingPdf}>
              {generatingPdf ? 'GENERATING...' : '↓ GENERATE PDF'}
            </button>
          )}
          {user.role === 'admin' && (
            <button className="mr-btn danger" onClick={async () => {
              if (!window.confirm('Permanently delete this management review and its PDF?')) return;
              await axios.delete(`${API}/management-reviews/${selected.id}`);
              setView('list');
              setSelected(null);
              fetchAll();
            }}>DELETE</button>
          )}
        </div>

        {selected.next_review_date && (
          <div className="mr-next-review">
            📅 Next management review: <strong>{formatDate(selected.next_review_date)}</strong>
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────
  return (
    <div className="mr-page">
      {/* Alert if overdue */}
      {reviews.length === 0 && (
        <div className="mr-alert">
          ⚠ No management review on record — one must be completed before the June audit
        </div>
      )}

      <div className="mr-controls">
        {user.role === 'admin' && (
          <button className="mr-new-btn" onClick={() => { setActiveSection('details'); setView('new'); }}>
            + NEW MANAGEMENT REVIEW
          </button>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="mr-empty">
          <div className="mr-empty-icon">📋</div>
          <p>No management reviews recorded yet.</p>
          {user.role === 'admin' && (
            <button className="mr-btn primary" onClick={() => { setActiveSection('details'); setView('new'); }}>
              Start your first management review
            </button>
          )}
        </div>
      ) : (
        <div className="mr-list">
          {reviews.map(review => {
            const objData = review.objectives_review || [];
            const achieved = objData.filter(o => o.status_after === 'achieved').length;
            return (
              <div key={review.id} className="mr-card" onClick={() => { setSelected(review); setView('detail'); }}>
                <div className="mr-card-top">
                  <div>
                    <div className="mr-card-ref">{review.reference_number}</div>
                    <div className="mr-card-title">{review.title}</div>
                    <div className="mr-card-meta">
                      {formatDate(review.date)}
                      {review.chaired_by && ` · Chaired by ${review.chaired_by.first_name} ${review.chaired_by.last_name}`}
                    </div>
                  </div>
                  <div className="mr-card-right">
                    <span className={`mr-status-badge ${review.status === 'completed' ? 'completed' : 'draft'}`}>
                      {review.status === 'completed' ? 'Completed' : 'Draft'}
                    </span>
                    {objData.length > 0 && (
                      <span className="mr-obj-count">{achieved}/{objData.length} objectives achieved</span>
                    )}
                  </div>
                </div>
                {review.next_review_date && (
                  <div className="mr-next-review-chip">
                    Next review: {formatDate(review.next_review_date)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
